/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAlert from "components/MDAlert";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import DataTable from "../../examples/Tables/DataTable";
import useApi from "../../components/Utils/api";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import PermissionControl from "../../components/Utils/PermissionControl";
import {useTranslation} from "react-i18next";
import PropTypes from "prop-types";
import {formatDate} from "../../utils"
function MerchantGroups() {
  // const { merchantGroupTablecolumns } = data();
  const {t} = useTranslation();
  const [merchants, setMerchants] = useState([]);
  const [filteredMerchants, setFilteredMerchants] = useState([]);
  const navigate = useNavigate();
  const api = useApi();

  // Filter state
  const [filterSearch, setFilterSearch] = useState("");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");
  const getMerchantGroups = () => {
    api.get("admin/browseMerchantGroup")
      .then((response) => {
        if(response.data.statusCodeValue === 200) {
          setMerchants(response.data.body);
        } else {
          toast.error(response.data.error);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error(t('you_do_not_have_permission_to_perform_this_action'), { id: "PermissionError" });
          return;
        }
      })
  }
    const rowHandler = (org)  => {
        navigate("/merchant-group/single", {
            state: {
                referenceCompany: org.row.original,
                merchantGroups: merchants
            }
        })
    }
  const merchantGroupTablecolumns = [
    { Header: t("merchantGroupTablecolumns.referenceCompanyName"), accessor: "referenceCompanyName", width: "10%", align: "left" },
    { Header: t("merchantGroupTablecolumns.ownerName"), accessor: "ownerName", width: "10%", align: "left" },
    { Header: t("merchantTablecolumns.created_at"), accessor: 'createdDt', Cell: ({cell}) => formatDate(cell.value), width: "10%", align: "left" },
    { Header: t("merchantGroupTablecolumns.status"), accessor: row => row.status === "1" ? 'Active' : row.status === "3" ? 'Block' : 'In Active', width: "10%", align: "left" },
  ];
  useEffect(() => {
    getMerchantGroups();
  },[]);

  useEffect(() => {
    applyFilters();
  }, [merchants, filterSearch, filterFromDate, filterToDate]);

  const statusLabel = (status) => status === "1" ? "Active" : status === "3" ? "Block" : "In Active";

  const applyFilters = () => {
    let result = [...merchants];
    const search = filterSearch.trim().toLowerCase();
    if (search) {
      result = result.filter(m =>
        m.referenceId?.toLowerCase().includes(search) ||
        m.referenceCompanyName?.toLowerCase().includes(search) ||
        m.ownerName?.toLowerCase().includes(search) ||
        statusLabel(m.status).toLowerCase().includes(search)
      );
    }
    if (filterFromDate) {
      result = result.filter(m => m.createdDt && new Date(m.createdDt) >= new Date(filterFromDate));
    }
    if (filterToDate) {
      const toDateEnd = new Date(filterToDate);
      toDateEnd.setHours(23, 59, 59, 999);
      result = result.filter(m => m.createdDt && new Date(m.createdDt) <= toDateEnd);
    }
    setFilteredMerchants(result);
  };

  const resetFilters = () => {
    setFilterSearch("");
    setFilterFromDate("");
    setFilterToDate("");
  };

  return (
    <DashboardLayout>
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <Grid container justifyContent="space-between" alignItems="center">
                <MDBox>
                    <MDTypography variant="h5" fontWeight="medium">
                        {t('organizations')}
                    </MDTypography>
                    <MDTypography variant="button" fontWeight="regular" color="secondary">
                        {filteredMerchants.length} {t('organizations')}
                    </MDTypography>
                </MDBox>
                <MDBox display="flex">
                    <PermissionControl permission={{ restrict: true, module: "Organization", subModule: "Create" }}>
                      <MDButton color="info" variant="gradient" onClick={() => navigate("/merchant-group/single", { state: { merchantGroups: merchants }  })}>{t('new_organization')}</MDButton>
                    </PermissionControl>
                </MDBox>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={12}>
            <MDBox sx={(theme) => ({ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: "8px", background: theme.palette.white.main, overflow: "hidden" })}>
            {/* Filter Panel */}
            <MDBox px={2} py={1.5} sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.grey[300]}`, background: theme.palette.background.hover })}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <TextField
                    label={t('search')}
                    value={filterSearch}
                    onChange={(e) => setFilterSearch(e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <TextField
                    label={t('from_date')}
                    type="date"
                    value={filterFromDate}
                    onChange={(e) => setFilterFromDate(e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ shrink: true, style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg={2}>
                  <TextField
                    label={t('to_date')}
                    type="date"
                    value={filterToDate}
                    onChange={(e) => setFilterToDate(e.target.value)}
                    fullWidth
                    size="small"
                    variant="outlined"
                    InputProps={{ style: { fontSize: "0.8rem" } }}
                    InputLabelProps={{ shrink: true, style: { fontSize: "0.8rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { height: "38px" } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3} lg="auto">
                  <MDButton variant="outlined" color="secondary" onClick={resetFilters} size="small" style={{ fontSize: "0.75rem", padding: "5px 16px", whiteSpace: "nowrap" }}>
                    {t('reset')}
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
            <DataTable
              table={{ columns : merchantGroupTablecolumns, rows : filteredMerchants }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              entriesPerPagePosition="bottom"
              canSearch={false}
              onRowClicked={(row) => rowHandler({ row })}
              disableRowDialog
            />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
MerchantGroups.propTypes = {
  cell: PropTypes.shape({
    value: PropTypes.any.isRequired,
  }),
};
export default MerchantGroups;
